import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { SearchableSelect } from '@/components/SearchableSelect';
import { DocumentsSection } from '@/components/documents/DocumentsSection';
import { Plus, FileText, Trash2, ArrowRight, FileDown, Eye, Lock, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useConfirm } from '@/components/ConfirmDialog';
import { STATUSES, STATE_OPTIONS, PAYMENT_STYLES, PROJECT_TYPES, StatusPill, itemState, money } from './utils';

export default function ProposalDetailSheet({ open, onOpenChange, selected, setSelected, clients, properties, clientMap, onChanged, onRequestCreateProject }) {
  const navigate = useNavigate();
  const { confirm } = useConfirm();

  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const fetchSyncStatus = useCallback(() => {
    if (!selected?.id || !selected?.linked_project) { setSyncStatus(null); return; }
    api.get(`/proposals/${selected.id}/sync-status`)
      .then(r => setSyncStatus(r.data))
      .catch(() => setSyncStatus(null));
  }, [selected?.id, selected?.linked_project]);
  useEffect(() => { if (open) fetchSyncStatus(); }, [open, fetchSyncStatus, selected?.line_items]);

  const handleSyncToProject = async () => {
    const n = syncStatus?.new_count || 0;
    if (!n) return;
    const ok = await confirm({
      title: 'Sync new items to project',
      description: `${n} new line item${n === 1 ? '' : 's'} will be added to the project's Scope Checklist. Existing checklist items will not be changed.`,
      confirmLabel: `Add ${n} item${n === 1 ? '' : 's'}`,
    });
    if (!ok) return;
    setSyncing(true);
    try {
      const r = await api.post(`/proposals/${selected.id}/sync-to-project`);
      toast.success(`Added ${r.data.added} item${r.data.added === 1 ? '' : 's'} to the Scope Checklist`);
      fetchSyncStatus();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not sync items to project');
    }
    setSyncing(false);
  };

  const saveField = async (patch) => {
    setSelected(prev => ({ ...prev, ...patch }));
    try { const r = await api.put(`/proposals/${selected.id}`, patch); setSelected(prev => ({ ...prev, ...r.data })); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };
  const setLocalItem = (lid, patch) => setSelected(prev => ({ ...prev, line_items: (prev.line_items || []).map(it => it.line_item_id === lid ? { ...it, ...patch } : it) }));
  const saveLineItem = async (lid, patch) => {
    setLocalItem(lid, patch);
    try { const r = await api.put(`/proposals/${selected.id}/line-items/${lid}`, patch); setSelected(prev => ({ ...prev, ...r.data })); }
    catch { toast.error('Could not save line item'); }
  };
  const addDetailItem = async () => {
    try { const r = await api.post(`/proposals/${selected.id}/line-items`, { description: '', scope_detail: '', fee: 0, item_state: 'included' }); setSelected(prev => ({ ...prev, ...r.data })); }
    catch { toast.error('Could not add line item'); }
  };
  const deleteDetailItem = async (lid) => {
    try { const r = await api.delete(`/proposals/${selected.id}/line-items/${lid}`); setSelected(prev => ({ ...prev, ...r.data })); }
    catch { toast.error('Could not delete line item'); }
  };

  const exportPdf = async () => {
    try {
      const r = await api.get(`/proposals/${selected.id}/export-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `Proposal_${(selected.proposal_number || 'draft').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Could not export PDF'); }
  };
  const previewPdf = async () => {
    try {
      const r = await api.get(`/proposals/${selected.id}/export-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch { toast.error('Could not open preview'); }
  };

  const detailItems = selected?.line_items || [];
  const dSubtotal = detailItems.filter(i => itemState(i) === 'included').reduce((s, i) => s + (Number(i.fee) || 0), 0);
  const dConditional = detailItems.filter(i => itemState(i) === 'conditional').reduce((s, i) => s + (Number(i.fee) || 0), 0);
  const dTotal = dSubtotal + (Number(selected?.contingency_amount) || 0);

  const isLocked = !!selected?.linked_project;
  const clientOptions = (Array.isArray(clients) ? clients : []).map(c => ({ value: c.id, label: c.name }));
  const propertyOptions = (Array.isArray(properties) ? properties : []).map(p => ({ value: p.id, label: `${p.display_name || p.address}${p.borough ? ` : ${p.borough}` : ''}` }));
  const clientName = clientMap[selected?.client_id] || '';
  const selProp = (Array.isArray(properties) ? properties : []).find(p => p.id === selected?.property_id);
  const propertyName = selProp ? `${selProp.display_name || selProp.address}${selProp.borough ? ` : ${selProp.borough}` : ''}` : (selected?.property_address || '');

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onOpenChange(false); }}>
      <SheetContent className="bg-card border-border w-full sm:max-w-3xl overflow-y-auto" data-testid="proposal-detail-sheet">
        {selected && (
          <>
            <SheetHeader>
              <div className="flex items-center gap-3 flex-wrap">
                <SheetTitle style={{ color: '#010A21' }} data-testid="proposal-detail-title">{selected.title || 'Untitled Proposal'}</SheetTitle>
                <StatusPill status={selected.status} />
              </div>
              <SheetDescription>
                {selected.proposal_number || 'Draft'} · {clientMap[selected.client_id] || 'No client'}{selected.project_type ? ` · ${selected.project_type}` : ''}
              </SheetDescription>
            </SheetHeader>

            <div className="flex items-center gap-2 mt-4 flex-wrap">
              <Select value={selected.status} onValueChange={v => saveField({ status: v })}>
                <SelectTrigger className="w-44 bg-background border-border h-9 text-sm" data-testid="proposal-status-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">{STATUSES.map(s => <SelectItem key={s} value={s} className="text-sm">{s}</SelectItem>)}</SelectContent>
              </Select>
              <Button size="sm" variant="outline" className="border-border" onClick={previewPdf} data-testid="proposal-preview-pdf"><Eye className="h-3.5 w-3.5 mr-1" /> Preview</Button>
              <Button size="sm" variant="outline" className="border-border" onClick={exportPdf} data-testid="proposal-export-pdf"><FileDown className="h-3.5 w-3.5 mr-1" /> Export PDF</Button>
              {selected.linked_project ? (
                <>
                  <Button size="sm" variant="outline" className="border-border" onClick={() => navigate(`/projects/${selected.linked_project.id}`)} data-testid="proposal-view-project">View Project <ArrowRight className="h-3.5 w-3.5 ml-1" /></Button>
                  {(syncStatus?.new_count || 0) > 0 && (
                    <Button size="sm" variant="outline" className="border-primary text-primary hover:bg-primary/5" onClick={handleSyncToProject} disabled={syncing} data-testid="proposal-sync-to-project">
                      <RefreshCw className={`h-3.5 w-3.5 mr-1 ${syncing ? 'animate-spin' : ''}`} /> Sync {syncStatus.new_count} New Item{syncStatus.new_count === 1 ? '' : 's'} to Project
                    </Button>
                  )}
                </>
              ) : selected.status === 'Accepted' ? (
                <Button size="sm" className="bg-primary text-primary-foreground" onClick={onRequestCreateProject} data-testid="proposal-create-project"><FileText className="h-3.5 w-3.5 mr-1" /> Create Project from Proposal</Button>
              ) : null}
            </div>

            <div className="mt-5 rounded-md border border-border bg-secondary/30 p-3" data-testid="proposal-meta-fields">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Client</Label>
                  {isLocked ? (
                    <p className="text-sm mt-1.5 font-medium" style={{ color: '#010A21' }} data-testid="proposal-client-readonly">{clientName || '\u00A0'}</p>
                  ) : (
                    <div className="mt-1"><SearchableSelect options={clientOptions} value={selected.client_id || ''} onChange={v => saveField({ client_id: v })} placeholder="Select client" searchPlaceholder="Search clients..." emptyText="No clients." testid="proposal-client-select" /></div>
                  )}
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Property</Label>
                  {isLocked ? (
                    <p className="text-sm mt-1.5 font-medium" style={{ color: '#010A21' }} data-testid="proposal-property-readonly">{propertyName || 'No property'}</p>
                  ) : (
                    <div className="mt-1"><SearchableSelect options={propertyOptions} value={selected.property_id || ''} onChange={v => saveField({ property_id: v })} placeholder="Select property (optional)" searchPlaceholder="Search properties..." emptyText="No properties." testid="proposal-property-select" /></div>
                  )}
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Project Type</Label>
                  {isLocked ? (
                    <p className="text-sm mt-1.5 font-medium" style={{ color: '#010A21' }} data-testid="proposal-project-type-readonly">{selected.project_type || '\u00A0'}</p>
                  ) : (
                    <Select value={selected.project_type || ''} onValueChange={v => saveField({ project_type: v })}>
                      <SelectTrigger className="bg-background border-border mt-1" data-testid="proposal-project-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent className="bg-card border-border">{PROJECT_TYPES.map(t => <SelectItem key={t} value={t} className="text-sm">{t}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </div>
              </div>
              {isLocked && (
                <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1" data-testid="proposal-lock-note"><Lock className="h-3 w-3" /> Locked after project creation.</p>
              )}
            </div>

            <div className="mt-5">
              <Label className="text-xs text-muted-foreground">Cover Description</Label>
              <Textarea value={selected.cover_description || ''} onChange={e => setSelected({ ...selected, cover_description: e.target.value })} onBlur={e => saveField({ cover_description: e.target.value })} rows={4} placeholder="Introductory description shown at the top of the proposal PDF" className="bg-background border-border mt-1 text-sm" data-testid="proposal-cover-description" />
            </div>

            <div className="mt-4">
              <Label className="text-xs text-muted-foreground">Payment Terms</Label>
              <Textarea value={selected.payment_terms || ''} onChange={e => setSelected({ ...selected, payment_terms: e.target.value })} onBlur={e => saveField({ payment_terms: e.target.value })} rows={3} placeholder="e.g. 50% due upon signing, balance due upon filing" className="bg-background border-border mt-1 text-sm" data-testid="proposal-payment-terms" />
            </div>

            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold" style={{ color: '#010A21' }}>Scope &amp; Fees</p>
                <Button size="sm" variant="outline" className="border-border" onClick={addDetailItem} data-testid="proposal-add-line-item"><Plus className="h-3.5 w-3.5 mr-1" /> Add Line Item</Button>
              </div>
              <div className="border border-border rounded-md overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border hover:bg-transparent">
                      <TableHead className="w-8 text-xs text-muted-foreground">#</TableHead>
                      <TableHead className="text-xs text-muted-foreground">Description</TableHead>
                      <TableHead className="w-24 text-xs text-muted-foreground text-right">Fee</TableHead>
                      <TableHead className="w-28 text-xs text-muted-foreground">Payment</TableHead>
                      <TableHead className="w-32 text-xs text-muted-foreground">State</TableHead>
                      <TableHead className="w-8"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detailItems.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-6">No line items yet.</TableCell></TableRow>
                    ) : detailItems.map(it => {
                      const st = itemState(it);
                      const ps = it.payment_status || 'unpaid';
                      const pstyle = PAYMENT_STYLES[ps];
                      return (
                        <TableRow key={it.line_item_id} className="border-border hover:bg-transparent align-top" style={{ opacity: st === 'excluded' ? 0.4 : 1 }} data-testid={`line-item-${it.line_item_id}`}>
                          <TableCell className="text-sm text-muted-foreground pt-3">{it.number}</TableCell>
                          <TableCell className="py-2">
                            <Input value={it.description || ''} onChange={e => setLocalItem(it.line_item_id, { description: e.target.value })} onBlur={e => saveLineItem(it.line_item_id, { description: e.target.value })} className="bg-background border-border h-8 text-sm" data-testid={`line-item-desc-${it.line_item_id}`} />
                            <Input value={it.scope_detail || ''} onChange={e => setLocalItem(it.line_item_id, { scope_detail: e.target.value })} onBlur={e => saveLineItem(it.line_item_id, { scope_detail: e.target.value })} placeholder="Scope detail" className="bg-background border-border h-7 text-xs mt-1 text-muted-foreground" />
                            {st === 'conditional' && (
                              <Input value={it.condition_note || ''} onChange={e => setLocalItem(it.line_item_id, { condition_note: e.target.value })} onBlur={e => saveLineItem(it.line_item_id, { condition_note: e.target.value })} placeholder="Condition (e.g. Per application if required)" className="bg-background border-border h-7 text-xs mt-1 italic" style={{ color: '#031333' }} data-testid={`line-item-condition-${it.line_item_id}`} />
                            )}
                          </TableCell>
                          <TableCell className="py-2"><Input type="number" value={it.fee ?? 0} onChange={e => setLocalItem(it.line_item_id, { fee: e.target.value })} onBlur={e => saveLineItem(it.line_item_id, { fee: Number(e.target.value) || 0 })} className="bg-background border-border h-8 text-sm text-right" style={{ fontFamily: "'DM Mono', monospace" }} data-testid={`line-item-fee-${it.line_item_id}`} /></TableCell>
                          <TableCell className="py-2">
                            <Select value={ps} onValueChange={v => saveLineItem(it.line_item_id, { payment_status: v })}>
                              <SelectTrigger className="h-7 border-0 text-xs" style={{ backgroundColor: pstyle.bg, color: pstyle.fg, borderRadius: '2px' }} data-testid={`line-item-payment-${it.line_item_id}`}><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-card border-border">{Object.entries(PAYMENT_STYLES).map(([v, s]) => <SelectItem key={v} value={v} className="text-sm">{s.label}</SelectItem>)}</SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="py-2">
                            <Select value={st} onValueChange={v => saveLineItem(it.line_item_id, { item_state: v })}>
                              <SelectTrigger className="bg-background border-border h-8 text-xs" data-testid={`line-item-state-${it.line_item_id}`}><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-card border-border">{STATE_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v} className="text-sm">{l}</SelectItem>)}</SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="py-2"><Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => deleteDetailItem(it.line_item_id)} data-testid={`line-item-del-${it.line_item_id}`}><Trash2 className="h-4 w-4" /></Button></TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              <div className="flex justify-end mt-3">
                <div className="w-72 space-y-2">
                  <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">Subtotal (included)</span><span style={{ fontFamily: "'DM Mono', monospace" }} data-testid="proposal-subtotal">{money(dSubtotal)}</span></div>
                  {dConditional > 0 && <div className="flex items-center justify-between text-xs" style={{ color: '#D97706' }} data-testid="proposal-conditional-total"><span>Additional services if required</span><span style={{ fontFamily: "'DM Mono', monospace" }}>{money(dConditional)}</span></div>}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Contingency</span>
                    <Input type="number" value={selected.contingency_amount ?? 0} onChange={e => setSelected({ ...selected, contingency_amount: e.target.value })} onBlur={e => saveField({ contingency_amount: Number(e.target.value) || 0 })} className="bg-background border-border h-8 w-28 text-right text-sm" style={{ fontFamily: "'DM Mono', monospace" }} data-testid="proposal-contingency" />
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Retainer</span>
                    <Input type="number" value={selected.retainer_amount ?? 0} onChange={e => setSelected({ ...selected, retainer_amount: e.target.value })} onBlur={e => saveField({ retainer_amount: Number(e.target.value) || 0 })} className="bg-background border-border h-8 w-28 text-right text-sm" style={{ fontFamily: "'DM Mono', monospace" }} data-testid="proposal-retainer" />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-xs">Credit card surcharge</span>
                    <div className="flex items-center gap-2">
                      <Input type="number" value={selected.credit_card_surcharge_pct ?? 15} onChange={e => setSelected({ ...selected, credit_card_surcharge_pct: e.target.value })} onBlur={e => saveField({ credit_card_surcharge_pct: Number(e.target.value) || 0 })} className="bg-background border-border h-8 w-16 text-right text-sm" data-testid="proposal-surcharge-pct" />
                      <Switch checked={!!selected.show_surcharge} onCheckedChange={v => saveField({ show_surcharge: v })} data-testid="proposal-surcharge-toggle" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-border">
                    <span className="font-medium text-sm" style={{ color: '#010A21' }}>Total</span>
                    <span className="font-semibold" style={{ fontFamily: "'DM Mono', monospace", fontSize: '12px', color: '#010A21' }} data-testid="proposal-total">{money(dTotal)}</span>
                  </div>
                  {selected.show_surcharge && <p className="text-xs text-muted-foreground" data-testid="proposal-surcharge-note">{selected.credit_card_surcharge_pct || 15}% surcharge applies to credit card payments.</p>}
                </div>
              </div>
            </div>

            <div className="mt-6">
              <p className="text-sm font-semibold mb-2" style={{ color: '#010A21' }}>Documents</p>
              <DocumentsSection recordType="Proposal" recordId={selected.id} contextPropertyId={selected.property_id} />
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
